import React from "react";
import { FiExternalLink, FiLoader } from "react-icons/fi";

import Drawer from "./Drawer";
import { stripBuilderMarker } from "./builder/pageBuilderUtils";
import { toPageHref } from "../../../../services/pagesServices";

export default function PagePreviewDrawer({ open, loading, page, onClose }) {
  const cleanHtml = stripBuilderMarker(page?.content_html || "");
  const publicHref = page?.slug ? toPageHref(page.slug, page.locale || "fr") : "";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Aperçu de la page"
      subtitle={
        page?.title
          ? `${page.title} — aperçu du contenu tel qu’il est généré par le builder.`
          : "Chargement de l’aperçu…"
      }
      size="full"
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Fermer
          </button>

          {publicHref ? (
            <a
              href={publicHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-sky-700"
            >
              <FiExternalLink />
              Ouvrir la vraie page
            </a>
          ) : null}
        </div>
      }
    >
      {loading ? (
        <div className="flex min-h-[50vh] items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
          <div className="flex items-center gap-3">
            <FiLoader className="animate-spin text-xl" />
            Chargement de l’aperçu…
          </div>
        </div>
      ) : !page ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200">
          Page introuvable.
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="grid gap-2 text-sm text-slate-600 dark:text-slate-300 md:grid-cols-3">
              <Info label="Titre" value={page.title} />
              <Info label="Slug" value={`/pages/${page.slug}`} />
              <Info
                label="Statut"
                value={page.is_active ? "Page active" : "Page inactive"}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-[32px] border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-950">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-rose-400" />
                <span className="h-3 w-3 rounded-full bg-amber-400" />
                <span className="h-3 w-3 rounded-full bg-emerald-400" />

                <div className="ml-3 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  {publicHref || "/pages/..."}
                </div>
              </div>
            </div>

            <div className="bg-white py-8 dark:bg-white">
              {cleanHtml ? (
                <div
                  className="cms-page-preview text-slate-950"
                  dangerouslySetInnerHTML={{ __html: cleanHtml }}
                />
              ) : (
                <div className="mx-auto max-w-[1100px] px-4 py-16 text-center">
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-slate-500">
                    Aucun contenu n’a encore été créé dans le builder.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-2xl bg-white px-4 py-3 shadow-sm dark:bg-slate-950">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-1 truncate font-bold text-slate-800 dark:text-slate-100">
        {value || "—"}
      </div>
    </div>
  );
}